import {
  faArrowRightToBracket,
  faArrowUpRightFromSquare,
  faLockAlt,
  faStar,
  faTrashAlt,
  faUser,
  faLanguage,
  faChevronRight,
  faLinkSimple,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGridRound } from '@fortawesome/sharp-light-svg-icons';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { profileColours } from '../../../assets/colours';
import { Link } from '../../../shared/components/link/Link';
import LoadingSpinner from '../../../shared/components/UIElements/LoadingSpinner';
import { AuthContext } from '../../../shared/context/auth-context';
import { useTabs } from '../../../shared/hooks/useTabs';
import { findFirstFocusableElement } from '../../../shared/util/focusHelpers';
import { ActionButton } from '../../components/actionButton/ActionButton';
import { ImageUploadBtn } from './ImageUploadBtn';
import './AccountDetails.css';

type AccountDetailsLeftMenuProps = {
  profile: any;
  changeMenu: (arg0: number, arg1?: string) => void;
  activeSubMenu: { id: number };
  changeSubMenu: (arg0: number) => void;
  isHidden: boolean;
  saveProfileImage: (url: string) => void;
};

export function AccountDetailsLeftMenu({
  profile,
  changeMenu,
  activeSubMenu,
  changeSubMenu,
  isHidden,
  saveProfileImage,
}: AccountDetailsLeftMenuProps) {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  // Check if user is SSO (not ChampionHealth)
  const isNonSSOUser = auth.authType == null || auth.authType === 'ChampionHealth';

  // Define all tabs - Account Details, Languages, Change Password, Change Passcode
  const allTabs = [
    { id: 'tab-account-details', index: 0, subMenuId: 51 },
    { id: 'tab-languages', index: 6, subMenuId: 57 },
    { id: 'tab-change-password', index: 1, subMenuId: 52 },
    { id: 'tab-change-passcode', index: 2, subMenuId: 53 },
  ];

  // Filter tabs based on SSO status - only show visible tabs for keyboard navigation
  const tabs = isNonSSOUser
    ? allTabs
    : allTabs.filter((tab) => tab.subMenuId === 51 || tab.subMenuId === 57);

  let activeTabId = tabs[0]?.id || 'tab-account-details';
  if (activeSubMenu.id === 51) {
    activeTabId = 'tab-account-details';
  } else if (activeSubMenu.id === 57) {
    activeTabId = 'tab-languages';
  } else if (activeSubMenu.id === 52 && isNonSSOUser) {
    activeTabId = 'tab-change-password';
  } else if (activeSubMenu.id === 53 && isNonSSOUser) {
    activeTabId = 'tab-change-passcode';
  }

  // Custom handler for tab changes - all tabs are in Account menu
  const handleTabChange = (tabIndex: number) => {
    // Ensure we're in Account menu, then change subMenu
    changeMenu(0);
    setTimeout(() => changeSubMenu(tabIndex), 0);
  };

  // Map tab IDs to their corresponding panel IDs
  const tabToPanelMap: { [key: string]: string } = {
    'tab-account-details': 'panel-account-details',
    'tab-languages': 'panel-languages',
    'tab-change-password': 'panel-change-password',
    'tab-change-passcode': 'panel-change-passcode',
  };

  // Handler to focus the panel when tab is activated
  const handleFocusPanel = (tabId: string) => {
    const panelId = tabToPanelMap[tabId];
    if (panelId) {
      const focusableElement = findFirstFocusableElement(panelId);
      if (focusableElement) {
        focusableElement.focus();
      }
    }
  };

  const { handleKeyDown, setTabRef } = useTabs({
    tabs,
    activeTabId,
    onTabChange: handleTabChange,
    onFocusPanel: handleFocusPanel,
  });

  if (!profile) return null;

  const isAccountDetailsActive = activeSubMenu.id === 51 && !isHidden;
  const isLanguagesActive = activeSubMenu.id === 57 && !isHidden;
  const isChangePasswordActive = activeSubMenu.id === 52 && !isHidden;
  const isChangePasscodeActive = activeSubMenu.id === 53 && !isHidden;

  return (
    <>
      <ProfilePicture saveProfileImage={saveProfileImage} />
      <h2 className="profile-name fs-exclude">{profile.name}</h2>
      <div role="tablist" aria-label={t('page.title.settings')}>
        <button
          type="button"
          role="tab"
          id="tab-account-details"
          aria-controls="panel-account-details"
          aria-labelledby="profile-slider-btn-account-details"
          aria-selected={isAccountDetailsActive}
          className={`profile-slider-btn ${isAccountDetailsActive ? 'active' : ''}`}
          onClick={() => handleTabChange(0)}
          onKeyDown={(e) => handleKeyDown(e, 'tab-account-details')}
          ref={(el) => setTabRef('tab-account-details', el)}
        >
          <div className="menu-item">
            <FontAwesomeIcon
              icon={faUser}
              style={{ color: profileColours.blue }}
              className="slider-icon"
            />
            <div className="slider-text" id="profile-slider-btn-account-details">
              {t('S025')}
            </div>
          </div>
          <FontAwesomeIcon icon={faChevronRight} className="slider-chevron" aria-hidden="true" />
        </button>

        <button
          type="button"
          role="tab"
          id="tab-languages"
          aria-controls="panel-languages"
          aria-labelledby="profile-slider-btn-languages"
          aria-selected={isLanguagesActive}
          className={`profile-slider-btn ${isLanguagesActive ? 'active' : ''}`}
          onClick={() => handleTabChange(6)}
          onKeyDown={(e) => handleKeyDown(e, 'tab-languages')}
          ref={(el) => setTabRef('tab-languages', el)}
        >
          <div className="menu-item">
            <FontAwesomeIcon
              icon={faLanguage}
              style={{ color: profileColours.green }}
              className="slider-icon"
            />
            <div className="slider-text" id="profile-slider-btn-languages">
              {t('PL001')}
            </div>
          </div>
          <FontAwesomeIcon icon={faChevronRight} className="slider-chevron" aria-hidden="true" />
        </button>
        {(auth.authType == null || auth.authType === 'ChampionHealth') && (
          <>
            <button
              type="button"
              role="tab"
              id="tab-change-password"
              aria-controls="panel-change-password"
              aria-labelledby="profile-slider-btn-change-password"
              aria-selected={isChangePasswordActive}
              className={`profile-slider-btn ${isChangePasswordActive ? 'active' : ''}`}
              onClick={() => handleTabChange(1)}
              onKeyDown={(e) => handleKeyDown(e, 'tab-change-password')}
              ref={(el) => setTabRef('tab-change-password', el)}
            >
              <div className="menu-item">
                <FontAwesomeIcon
                  icon={faLockAlt}
                  style={{ color: profileColours.yellow }}
                  className="slider-icon"
                />
                <div className="slider-text" id="profile-slider-btn-change-password">
                  {t('S026')}
                </div>
              </div>
              <FontAwesomeIcon
                icon={faChevronRight}
                className="slider-chevron"
                aria-hidden="true"
              />
            </button>

            <button
              type="button"
              role="tab"
              id="tab-change-passcode"
              aria-controls="panel-change-passcode"
              aria-labelledby="profile-slider-btn-change-passcode"
              aria-selected={isChangePasscodeActive}
              className={`profile-slider-btn ${isChangePasscodeActive ? 'active' : ''}`}
              onClick={() => handleTabChange(2)}
              onKeyDown={(e) => handleKeyDown(e, 'tab-change-passcode')}
              ref={(el) => setTabRef('tab-change-passcode', el)}
            >
              <div className="menu-item">
                <FontAwesomeIcon
                  icon={faGridRound}
                  style={{ color: profileColours.yellow }}
                  className="slider-icon"
                />
                <div className="slider-text" id="profile-slider-btn-change-passcode">
                  {t('S028')}
                </div>
              </div>
              <FontAwesomeIcon
                icon={faChevronRight}
                className="slider-chevron"
                aria-hidden="true"
              />
            </button>
          </>
        )}
      </div>
      <div className="profile-action-btn-container">
        {auth.clientName && (
          <a
            href={`https://physitrack.typeform.com/first-feedback#client=${auth.clientName.replace(
              / /,
              '_'
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="profile-action-link"
          >
            <FontAwesomeIcon icon={faStar} className="action-icon" />

            {t('S005')}
          </a>
        )}

        <ActionButton
          color={profileColours.white}
          onClick={auth.logout}
          icon={faArrowRightToBracket}
          text={t('S006')}
        />
        <ActionButton
          onClick={() => changeSubMenu(3)}
          icon={faTrashAlt}
          text={t('S007')}
          color={profileColours.red}
        />
      </div>
      <ProfileFooter year={currentYear} t={t} />
    </>
  );
}

function ProfilePicture({ saveProfileImage }) {
  const auth = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div className="profile-photo fs-exclude">
      {isLoading && <LoadingSpinner asImage />}
      <img
        id="preview"
        src={auth.image}
        alt=""
        onLoad={() => setIsLoading(false)}
        style={{ display: isLoading ? 'none' : 'block' }}
      />
      <ImageUploadBtn saveProfileImage={saveProfileImage} />
    </div>
  );
}

function ProfileFooter({ year, t }) {
  const externalLinkIcon = {
    faIconDefinition: faArrowUpRightFromSquare,
    size: 'sm' as const,
  };

  const internalLinkIcon = {
    faIconDefinition: faLinkSimple,
    size: 'sm' as const,
  };

  return (
    <div className="profile-footer">
      <div className="privacy">&copy; {year} Champion Health</div>
      <Link
        href="/accessibility-statement"
        text={t('accessibility.statement.title')}
        icon={internalLinkIcon}
        iconPosition="right"
        className="profile-footer-link"
        ariaLabel={t('accessibility.statement.title')}
      />
      <Link
        href="https://www.championhealth.co.uk/legals/privacy-policy"
        text={t('S127')}
        target="_blank"
        rel="noopener noreferrer"
        icon={externalLinkIcon}
        iconPosition="right"
        className="profile-footer-link"
        ariaLabel={`${t('S127')} - ${t('external.link.label')}`}
      />
      <Link
        href="https://www.championhealth.co.uk/legals/terms-of-service"
        text={t('S131')}
        target="_blank"
        rel="noopener noreferrer"
        icon={externalLinkIcon}
        iconPosition="right"
        className="profile-footer-link"
        ariaLabel={`${t('S131')} - ${t('external.link.label')}`}
      />
      <Link
        href="https://support.championhealth.co.uk/"
        text={t('H004')}
        target="_blank"
        rel="noopener noreferrer"
        icon={externalLinkIcon}
        iconPosition="right"
        className="profile-footer-link"
        ariaLabel={`${t('H004')} - ${t('external.link.label')}`}
      />
    </div>
  );
}
